import { Calendar, Share2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Paisley } from "../HennaMotif";

const ADDRESS =
  "POİ Boğaz Bahçe 2 (Anadolu Yakası) Çatalmeşe, 30 Ağustos Cd. No: 3/15, 34406 Çekmeköy/İstanbul";
const QUERY = encodeURIComponent(ADDRESS);
const EMBED = `https://www.google.com/maps?q=${QUERY}&z=15&output=embed`;
const DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${QUERY}`;

const EVENT_TITLE = "Nurhan & Emre Düğünü";
const EVENT_DESC =
  "Kına düğünümüze davetlisiniz. Gecenin son bir saatinde kına gerçekleştirilecektir.";
// 15 Kasım 2026 19:00 (+03) -> 16:00 UTC; ~6 saat sonra biter
const START_UTC = "20261115T160000Z";
const END_UTC = "20261115T220000Z";

const GOOGLE_CAL = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
  EVENT_TITLE
)}&dates=${START_UTC}/${END_UTC}&details=${encodeURIComponent(
  EVENT_DESC
)}&location=${QUERY}`;

const shareDay = async () => {
  const shareData = {
    title: EVENT_TITLE,
    text: "15 Kasım 2026 · POİ Boğaz Bahçe 2",
    url: typeof window !== "undefined" ? window.location.href : "",
  };
  if (navigator.share) {
    try {
      await navigator.share(shareData);
    } catch {
      /* iptal edildi */
    }
    return;
  }
  try {
    await navigator.clipboard.writeText(shareData.url);
    toast.success("Bağlantı kopyalandı.");
  } catch {
    toast.error("Paylaşım desteklenmiyor.");
  }
};

const PillLink = ({ href, testid, Icon, children, download }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    download={download}
    data-testid={testid}
    className="group inline-flex items-center gap-2 rounded-full border border-gold/40 bg-ink/30 px-5 py-3 font-sans text-[0.7rem] uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:border-gold hover:bg-ink/60 hover:text-gold"
  >
    <Icon className="h-4 w-4 text-gold" />
    <span>{children}</span>
  </a>
);

const PillButton = ({ onClick, testid, Icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="group inline-flex items-center gap-2 rounded-full border border-gold/40 bg-ink/30 px-5 py-3 font-sans text-[0.7rem] uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:border-gold hover:bg-ink/60 hover:text-gold"
  >
    <Icon className="h-4 w-4 text-gold" />
    <span>{children}</span>
  </button>
);

export const Location = () => (
  <section
    id="mekan"
    data-testid="location-section"
    className="relative overflow-hidden bg-surface/50 py-32 md:py-44"
  >
    <Paisley className="pointer-events-none absolute -right-8 top-24 h-52 w-40 -rotate-12 text-gold/15" />

    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 md:px-10">
      <Reveal>
        <p className="mb-6 font-sans text-xs uppercase tracking-[0.35em] text-gold">
          Konum &amp; Yol Tarifi
        </p>
        <h2 className="font-serif text-4xl leading-tight tracking-tight text-cream md:text-6xl">
          Boğaz Bahçe 2
        </h2>
        <p className="mt-2 font-serif text-2xl italic text-sand">
          Anadolu Yakası · Çatalmeşe
        </p>

        <div className="mt-8 space-y-4 border-t border-line/60 pt-8 font-sans text-sand">
          <p className="text-base leading-relaxed">
            30 Ağustos Cd. No: 3/15, 34406
            <br />
            Çekmeköy / İstanbul
          </p>
          <p className="text-sm text-sand/80">
            15 Kasım, Pazar · Saat 19:00&apos;da kapılarımız açılıyor.
            <br />
            Düğünümüz yemeklidir. Gecenin son bir saatinde kına gerçekleştirilecektir.
          </p>
          <p className="flex items-center gap-2 text-sm text-gold">
            <span className="text-base">⚑</span>
            Düğün salonumuzun kendi otoparkı mevcuttur.
          </p>
        </div>

        <a
          data-testid="directions-button"
          href={DIRECTIONS}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-8 inline-flex items-center gap-3 rounded-full bg-henna px-7 py-4 font-sans text-xs uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-henna-hover"
        >
          Yol Tarifi Al
          <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </a>

        <div className="mt-5 flex flex-wrap gap-3" data-testid="location-actions">
          <PillLink href={GOOGLE_CAL} testid="google-calendar-button" Icon={Calendar}>
            Google Takvim
          </PillLink>
          <PillLink href="/davetiye.jpg" testid="invite-view-button" Icon={ImageIcon}>
            Davetiyeyi Gör
          </PillLink>
          <PillButton onClick={shareDay} testid="share-button" Icon={Share2}>
            Bu Günü Paylaş
          </PillButton>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="map-frame overflow-hidden rounded-sm border border-gold/20 shadow-2xl shadow-black/50">
          <iframe
            title="Düğün Konumu"
            src={EMBED}
            data-testid="location-map"
            width="100%"
            height="440"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </Reveal>
    </div>
  </section>
);
